// @react
import React, { useEffect } from 'react'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_CONFERENCES } from 'graphql/queries/learning.query'
import {
  ConferencesQuery,
  ConferencesQueryVariables,
  LearningActivityOrderByList,
} from 'lib/graphql/graphqlTypes'
// @common
import Typography from '@material-ui/core/Typography'
import { ExternalRouteNames } from 'constants/routeNames'
import SortUtils from 'utils/sortutils'
import { makeStyles } from '@material-ui/core/styles'
import Helper from 'utils/Helper.class'
// @components
import colors from '@bit/atd.web.colors'
import Lozenge from '@bit/atd.web.lozenge'
import Table from '@bit/atd.web.table'
import ButtonNav from '@bit/atd.web.button-nav'
import Box from '@bit/atd.web.box'
import Button from '@bit/atd.web.button'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
// @module components
import useTable from 'modules/common/hooks/useTable'
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import QueryError from 'modules/common/components/Errors/QueryError'
import PaginationScrollWrapper from 'modules/common/components/PaginationScrollWrapper'
import strings from 'lib/constants/strings'

/**
 * 4.0.1.3 MyATD - ActivityHistory - Learning - Conferences
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3123%3A16072
 *
 *
 *
 *
 *
 */

const useStyles = makeStyles({
  title: {
    display: 'block',
    fontWeight: 600,
    color: colors.black,
  },
  subtitle: {
    color: colors.gray500,
    fontWeight: 600,
  },
  textTitle: {
    color: '#2E2d31',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '26px',
    marginBottom: '8px',
  },
  textDescription: {
    color: '#424140',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
  textAction: {
    marginTop: '24px',
    flexGrow: 1,
    display: 'flex',
    '& > a': {
      alignSelf: 'flex-end',
    },
  },
})

const Conferences = () => {
  /******************************************
   * PROPS
   ******************************************/

  /******************************************
   * STATE
   ******************************************/

  /******************************************
   * HOOKS
   ******************************************/
  const {
    Pagination,
    paginationProps,
    ordering,
    inputVal,
    setRefetch,
    sortHandler,
  } = useTable<ConferencesQueryVariables>({
    orderBy: LearningActivityOrderByList.TITLE,
  })

  const { loading: isLoading, error: isError, data, refetch } = useQuery<
    ConferencesQuery,
    ConferencesQueryVariables
  >(GET_CONFERENCES, {
    variables: {
      input: inputVal,
    },
  })

  useEffect(() => setRefetch(refetch), [])

  const classes = useStyles()

  if (isError) {
    return <QueryError />
  }

  /******************************************
   * FUNCTIONS
   ******************************************/
  const meta = data?.conferences?.meta

  const tableRows = () => {
    if (!data?.conferences?.data) return [[<div key={1} />]]
    return data?.conferences?.data.map((item) => {
      const content = item
      return [
        <span key={1}>
          <strong className={classes.title}>{content.eventName}</strong>
        </span>,
        <Lozenge key={2} variant="outline" aria-label="In-Progress">
          {Helper.getStatus(content.startDate)}
        </Lozenge>,
        <span key={3}>
          <strong className={classes.title}>
            {Helper.rangeDateFormatter(content.startDate, content.endDate)}
          </strong>
          <strong className={classes.subtitle}>{content.location}</strong>
        </span>,
      ]
    })
  }

  /**
   *
   */
  const rowActions = () => {
    if (!data?.conferences?.data) {
      return [[<div key={1} />]]
    }

    return data?.conferences?.data?.map((item, index: number) => {
      return [
        <ButtonNav
          key={index}
          variant="secondary"
          label="View"
          href={item.productUrl}
          linkType="external"
        />,
      ]
    })
  }

  /**
   *
   */
  const renderContent = () => {
    if (data?.conferences?.data?.[0]) {
      return (
        <PaginationScrollWrapper {...paginationProps}>
          <Table
            defaultSort={SortUtils.transformSortValues(ordering)}
            header={[
              {
                label: strings.ACTIVITY_HISTORY.TABLE.TITLE,
                sort: sortHandler,
                mobileLayout: {
                  width: 'full',
                  ascLabel: strings.ACTIVITY_HISTORY.TABLE.ASC_LABEL_A_Z,
                  descLabel: strings.ACTIVITY_HISTORY.TABLE.DESC_LABEL_A_Z,
                  showLabel: false,
                },
              },
              {
                label: strings.ACTIVITY_HISTORY.TABLE.STATUS,
                sort: sortHandler,
                mobileLayout: {
                  width: 'full',
                  ascLabel: strings.ACTIVITY_HISTORY.TABLE.ASC_LABEL_A_Z,
                  descLabel: strings.ACTIVITY_HISTORY.TABLE.DESC_LABEL_A_Z,
                  showLabel: false,
                },
              },
              {
                label: strings.ACTIVITY_HISTORY.TABLE.DETAILS,
                mobileLayout: {
                  width: 'full',
                  showLabel: true,
                },
              },
            ]}
            rows={tableRows()}
            actions={{
              label: 'Actions',
              items: rowActions(),
            }}
          />
          <PaginationWrapper>
            <Pagination {...paginationProps} count={meta?.totalPages} />
          </PaginationWrapper>
        </PaginationScrollWrapper>
      )
    }
    return (
      <div>
        <Typography className={classes.textTitle} component={'h3'}>
          {strings.ACTIVITY_HISTORY.CONFERENCES.NO_RESULTS_MESSAGE}
        </Typography>
        <Typography className={classes.textDescription} component={'p'}>
          {strings.ACTIVITY_HISTORY.CONFERENCES.NO_RESULTS_DESCRIPTION}
        </Typography>
        <div className={classes.textAction}>
          <Button href={ExternalRouteNames.ATD_EVENTS}>
            {strings.ACTIVITY_HISTORY.CONFERENCES.SEE_CONFERENCES}
          </Button>
        </div>
      </div>
    )
  }

  /**
   *
   * @param count
   */
  const formatCount = (count?: number | null): string => {
    if (count) return `(${count})`
    return ''
  }

  /******************************************
   * RENDER
   ******************************************/
  return (
    <Box
      header={{
        title: `${strings.ACTIVITY_HISTORY.CONFERENCES.TITLE} ${formatCount(
          data?.conferences?.meta?.count,
        )}`,
      }}
    >
      <ConditionalLoader condition={!isLoading}>
        {renderContent()}
      </ConditionalLoader>
    </Box>
  )
}

export default Conferences
